import React, { useMemo, useState } from 'react';
import { BarChart3, ClipboardCheck, Gamepad2, ListChecks, LogOut, RefreshCw, Users } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { AttendanceInput, AttendanceRecord, Group, Lead, MeetingLocation, Participant } from '../../context/LeadAttendanceContext';
import RegisterAttendanceTab from './tabs/RegisterAttendanceTab';
import MemberListTab from './tabs/MemberListTab';
import GroupStatsTab from './tabs/GroupStatsTab';
import LanguageSwitcher from './LanguageSwitcher';
import GamesTab from './tabs/GamesTab';

type DashboardTab = 'attendance' | 'members' | 'stats' | 'games';

interface GroupDashboardProps {
  apiUrl: string;
  sessionToken: string;
  lead: Lead;
  group: Group;
  event?: any;
  hasMultipleGroups: boolean;
  participants: Participant[];
  isLoadingParticipants: boolean;
  isSubmittingAttendance: boolean;
  attendanceHistory: AttendanceRecord[];
  error?: string | null;
  notice?: string | null;
  onSwitchGroup: () => void;
  onRefreshParticipants: () => Promise<void>;
  onLogout: () => void;
  onRecordAttendance: (payload: {
    date: string;
    generalNotes: string;
    location?: MeetingLocation;
    attendances: AttendanceInput[];
  }) => Promise<void>;
}

const tabConfig: Array<{ id: DashboardTab; labelKey: string; icon: React.ReactNode }> = [
  { id: 'attendance', labelKey: 'tabAttendanceLabel', icon: <ClipboardCheck className="h-4 w-4" /> },
  { id: 'games', labelKey: 'tabGamesLabel', icon: <Gamepad2 className="h-4 w-4" /> },
  { id: 'members', labelKey: 'tabMembersLabel', icon: <Users className="h-4 w-4" /> },
  { id: 'stats', labelKey: 'tabStatsLabel', icon: <BarChart3 className="h-4 w-4" /> },
];

export default function GroupDashboard({
  apiUrl,
  sessionToken,
  lead,
  group,
  event,

  hasMultipleGroups,
  participants,
  isLoadingParticipants,
  isSubmittingAttendance,
  attendanceHistory,
  error,
  notice,
  onSwitchGroup,
  onRefreshParticipants,
  onLogout,
  onRecordAttendance,
}: GroupDashboardProps) {
  const [activeTab, setActiveTab] = useState<DashboardTab>('attendance');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const { t } = useTranslation();

  const subtitle = useMemo(() => {
    return t('participantsCount', { count: participants.length });
  }, [participants.length, t]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await onRefreshParticipants();
    setIsRefreshing(false);
  };

  return (
    <section className="min-h-screen bg-slate-50 px-4 py-4">
      <div className="mx-auto w-full max-w-5xl">
        <header className="mb-3 rounded-2xl bg-gradient-to-r from-brand-700 to-cyan-700 p-4 text-white shadow-card">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h2 className="text-xl font-semibold">{group.name}</h2>
              <p className="mt-1 text-sm text-cyan-50">{t('leadPrefix')}: {lead.name}</p>
              <p className="text-xs text-cyan-100">{subtitle}</p>
            </div>

            <div className="flex flex-wrap gap-2 items-center">
              <LanguageSwitcher variant="dark" />
              <button
                type="button"
                onClick={onSwitchGroup}
                className="inline-flex h-11 min-w-[48px] items-center justify-center rounded-xl border border-white/40 bg-white/10 px-3 text-sm font-medium text-white backdrop-blur-sm hover:bg-white/20"
              >
                <ListChecks className="mr-1.5 h-4 w-4" />
                {t('backToCeremonies')}
              </button>

              <button
                type="button"
                onClick={() => setActiveTab('games')}
                className="inline-flex h-11 min-w-[48px] items-center justify-center rounded-xl border border-white/40 bg-white/10 px-3 text-sm font-medium text-white backdrop-blur-sm hover:bg-white/20"
              >
                <Gamepad2 className="mr-1.5 h-4 w-4" />
                {t('tabGamesLabel')}
              </button>

              <button
                type="button"
                onClick={handleRefresh}
                className="inline-flex h-11 min-w-[48px] items-center justify-center rounded-xl border border-white/40 bg-white/10 px-3 text-sm font-medium text-white backdrop-blur-sm hover:bg-white/20"
              >
                <RefreshCw className={`mr-1.5 h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                {t('refresh')}
              </button>

              <button
                type="button"
                onClick={onLogout}
                className="inline-flex h-11 min-w-[48px] items-center justify-center rounded-xl border border-white/40 bg-white/10 px-3 text-sm font-medium text-white backdrop-blur-sm hover:bg-white/20"
              >
                <LogOut className="mr-1.5 h-4 w-4" />
                {t('logout')}
              </button>
            </div>
          </div>
        </header>

        {notice && (
          <p className="mb-3 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
            {notice}
          </p>
        )}

        {error && (
          <p className="mb-3 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
            {error}
          </p>
        )}

        <nav className="mb-3 flex flex-wrap gap-2 rounded-2xl border border-slate-200 bg-white p-2">
          {tabConfig.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`inline-flex h-11 min-w-[48px] items-center justify-center gap-1.5 whitespace-nowrap rounded-xl px-3 text-sm font-medium transition ${
                activeTab === tab.id
                  ? 'bg-brand-700 text-white'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              {tab.icon}
              {t(tab.labelKey)}
            </button>
          ))}
        </nav>

        <main className="rounded-2xl border border-slate-200 bg-white p-3 shadow-card sm:p-4">
          {activeTab === 'attendance' && (
            <RegisterAttendanceTab
              participants={participants}
              isLoadingParticipants={isLoadingParticipants}
              isSubmitting={isSubmittingAttendance}
              onBack={onSwitchGroup}
              onSubmitAttendance={onRecordAttendance}
            />
          )}

          {activeTab === 'members' && (
            <MemberListTab participants={participants} isLoadingParticipants={isLoadingParticipants} />
          )}

          {activeTab === 'stats' && <GroupStatsTab history={attendanceHistory} />}

          {activeTab === 'games' && event?.id && (
            <GamesTab
              apiUrl={apiUrl}
              eventId={String(event.id)}
              eventTitle={String(event.title || 'Ceremony')}
              sessionToken={sessionToken}
              leaderName={lead.name}
            />
          )}

          {activeTab === 'games' && !event?.id && (
            <p className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
              Selecione uma cerimonia para habilitar os jogos realtime.
            </p>
          )}
        </main>
      </div>
    </section>
  );
}
